import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ShoppingCart,
  Plus,
  Minus,
  Heart,
  Share2,
  Star,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useHybridCart } from "@/contexts/HybridCartContext";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import {
  CartNotification,
  useCartNotification,
} from "@/components/CartNotification";
import productService, { Product } from "@/services/productService";
import { formatCurrency } from "@/lib/utils";
import { gtmEvent, mapItemToGA4 } from "@/lib/gtm";
import { pixelViewContent, pixelAddToCart } from "@/lib/pixel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar, MapPin, Clock, Users } from "lucide-react";

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addToCart } = useHybridCart();
  const { notification, showNotification, hideNotification } =
    useCartNotification();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWtsModalOpen, setIsWtsModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [personalizedName, setPersonalizedName] = useState("");
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [tamanoSeleccionado, setTamanoSeleccionado] = useState<string>("");

  // Scroll automático al top cuando cambia el producto
  useScrollToTop({ trigger: id });

  const handleOpenWtsModal = () => {
    setIsWtsModalOpen(true);
  };

  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["product", id],
    queryFn: () => productService.getProduct(id!),
    enabled: !!id,
  });

  // GTM + Pixel: view_item cuando el producto carga
  useEffect(() => {
    if (!product) return;
    gtmEvent("view_item", {
      ecommerce: {
        currency: "USD",
        value: product.price,
        items: [mapItemToGA4({ id: product._id, name: product.title, price: product.price, type: product.type })],
      },
    });
    pixelViewContent({
      content_ids: [product._id],
      content_name: product.title,
      value: product.price,
    });
  }, [product?._id]);

  // Query para productos relacionados (mismas tags)
  const { data: relatedProducts } = useQuery({
    queryKey: ["relatedProducts", product?.tags],
    queryFn: () =>
      productService.getProducts({
        tags: product?.tags?.slice(0, 2), // Usar las primeras 2 tags
      }),
    enabled: !!product?.tags?.length,
  });

  // Obtener opciones de tamaño ESTÁNDAR (solo para el botón "Comprar ahora")
  // Los productos personalizables tienen precios diferentes en configuracionPersonalizable
  const opcionesTamano = product?.opcionesTamano;

  // Función para obtener el precio según el tamaño seleccionado
  const obtenerPrecio = (): number => {
    if (!product) return 0;

    if (opcionesTamano && opcionesTamano.length > 0 && tamanoSeleccionado) {
      const tamano = opcionesTamano.find((t) => t.id === tamanoSeleccionado);
      return tamano?.precio || product.price;
    }

    return product.price;
  };

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    if (
      newQuantity >= 1 &&
      (product?.type === "digital" || product?.type === "taller" || newQuantity <= product!.stock)
    ) {
      setQuantity(newQuantity);
    }
  };

  const handleConfirmWTS = () => {
    if (!email) return;

    const phone = "50761568575";
    const productUrl = window.location.href;

    const message = `Hola, vengo desde este producto:
${productUrl}

Mi email es: ${email}

Me gustaría recibir más información, por favor.`;

    const waLink = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(waLink, "_blank", "noopener,noreferrer");
    setIsWtsModalOpen(false);
    setEmail("");
  };

  const handlePersonalizar = () => {
    if (!product || !product.slug) {
      toast({
        title: "Error",
        description: "Este producto no tiene configuración de personalización",
        variant: "destructive",
      });
      return;
    }

    navigate(`/personalizar/${product.slug}`);
  };

  const handleAddToCart = async () => {
    if (!product) return;

    if (!product.isActive) {
      toast({
        title: "Producto no disponible",
        description: "Este producto no está disponible actualmente",
        variant: "destructive",
      });
      return;
    }

    // Solo validar stock para productos físicos
    if (product.type === "físico" && product.stock < quantity) {
      toast({
        title: "Stock insuficiente",
        description: `Solo hay ${product.stock} unidades disponibles`,
        variant: "destructive",
      });
      return;
    }

    // Obtener info del tamaño si aplica (el selector de UI está deshabilitado por ahora)
    const opcionesTamanoActual = product.opcionesTamano;

    try {
      // Obtener info del tamaño seleccionado
      const tamanoInfo = opcionesTamanoActual?.find(
        (t) => t.id === tamanoSeleccionado
      );
      const precioFinal = obtenerPrecio();

      // Create product object for hybrid cart
      const productImages =
        product.images?.length > 0 ? product.images : [product.image];
      const productForCart = {
        _id: product._id,
        name: product.title,
        price: precioFinal, // Usar precio según tamaño
        image: productImages[0],
        images: productImages,
        stock: product.stock,
        type: product.type, // Incluir tipo para diferenciar digital/físico
        active: product.isActive,
      };

      // Preparar personalización si existe
      const customization: any = {};
      if (personalizedName.trim()) {
        customization.personalizedName = personalizedName.trim();
      }
      if (tamanoInfo) {
        customization.tamano = {
          id: tamanoInfo.id,
          nombre: tamanoInfo.nombre,
          dimensiones: `${tamanoInfo.dimensiones.ancho}cm × ${tamanoInfo.dimensiones.alto}cm`,
          orientacion: tamanoInfo.orientacion,
        };
      }

      await addToCart(
        productForCart,
        quantity,
        Object.keys(customization).length > 0 ? customization : undefined
      );

      // GTM: add_to_cart
      gtmEvent("add_to_cart", {
        ecommerce: {
          currency: "USD",
          value: precioFinal * quantity,
          items: [mapItemToGA4({ id: product._id, name: product.title, price: precioFinal, quantity, type: product.type })],
        },
      });
      pixelAddToCart({
        content_ids: [product._id],
        content_name: product.title,
        value: precioFinal * quantity,
      });

      // Show cart notification instead of toast
      showNotification(product.title);

      // Limpiar el campo de personalización después de agregar
      setPersonalizedName("");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast({
        title: "Error",
        description: "No se pudo añadir el producto al carrito",
        variant: "destructive",
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.title,
          text: product?.description,
          url: window.location.href,
        });
      } catch (error) {
        // El usuario canceló el share
      }
    } else {
      // Fallback: copiar al clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Enlace copiado",
        description: "El enlace del producto se copió al portapapeles",
      });
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Producto no encontrado
          </h1>
          <p className="text-muted-foreground mb-6">
            El producto que buscas no existe o ha sido eliminado.
          </p>
          <Button onClick={() => navigate("/products")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Productos
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Imagen skeleton */}
          <div className="space-y-4">
            <Skeleton className="aspect-square w-full rounded-lg" />
            <div className="flex gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="w-20 h-20 rounded-md" />
              ))}
            </div>
          </div>

          {/* Info skeleton */}
          <div className="space-y-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/4" />
              <Skeleton className="h-20 w-full" />
            </div>
            <Skeleton className="h-12 w-1/3" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const isOutOfStock = product.type === "físico" && product.stock === 0;
  const maxQuantity = (product.type === "digital" || product.type === "taller") ? 1 : product.stock;

  // Usar images si existe, sino usar image (compatibilidad)
  const productImages =
    product.images?.length > 0 ? product.images : [product.image];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">
          Inicio
        </Link>
        <span>/</span>
        <Link to="/products" className="hover:text-foreground">
          Productos
        </Link>
        <span>/</span>
        <span className="text-foreground">{product.title}</span>
      </nav>

      {/* Botón Volver */}
      <Button
        variant="ghost"
        onClick={() => navigate("/products")}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a Productos
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Galería de Imágenes */}
        <div className="space-y-4">
          <div className="relative aspect-square rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 p-4 flex items-center justify-center">
            <img
              src={productImages[selectedImage]}
              alt={product.title}
              className="object-contain w-full h-full rounded-lg"
            />
            {product.type === "digital" && (
              <Badge className="absolute top-4 right-4 bg-primary text-brown">
                Digital
              </Badge>
            )}
          </div>

          {/* Thumbnails (para futuras implementaciones) */}
          {productImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-md border-2 bg-gradient-to-br from-gray-50 to-gray-100 p-1 flex items-center justify-center ${
                    selectedImage === index
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-gray-200"
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.title} ${index + 1}`}
                    className="object-contain w-full h-full rounded-sm"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Información del Producto */}
        <div className="space-y-6">
          {/* Título y Rating */}
          <div>
            <div className="flex items-start justify-between mb-2">
              <h1 className="text-3xl font-playfair font-bold text-brown">
                {product.title}
              </h1>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleShare}>
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm">
                  <Heart className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-yellow-400 text-yellow-400"
                  />
                ))}
                <span className="text-sm text-muted-foreground ml-2">
                  (Sin reseñas)
                </span>
              </div>
            </div>
          </div>

          {/* Descripción Colapsable */}
          <div className="space-y-2">
            <p
              className={`text-muted-foreground text-lg leading-relaxed whitespace-pre-line ${
                !isDescriptionExpanded ? "line-clamp-4" : ""
              }`}
            >
              {product.description}
            </p>
            {product.description && product.description.length > 200 && (
              <button
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="text-primary hover:underline text-sm font-medium transition-colors"
              >
                {isDescriptionExpanded ? "Ver menos" : "Leer más..."}
              </button>
            )}
          </div>

          {/* Tags */}
          {product.tags && product.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-2">Etiquetas:</h3>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Card Destacado con Precio, Personalización y Compra */}
          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardContent className="p-6 space-y-4">
              {/* Precio */}
              <div className="space-y-2">
                <div className="text-3xl font-bold text-brown">
                  {formatCurrency(obtenerPrecio())}
                </div>

                {product.type === "físico" && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Stock disponible:
                    </span>
                    <Badge
                      variant={product.stock > 5 ? "default" : "destructive"}
                    >
                      {product.stock} unidades
                    </Badge>
                  </div>
                )}

                {product.type === "digital" && (
                  <Badge className="bg-green-100 text-green-800">
                    Descarga inmediata
                  </Badge>
                )}

                {product.type === "taller" && (
                  <Badge className="bg-amber-100 text-amber-800">
                    🎨 Taller
                  </Badge>
                )}
              </div>

              {/* Badge de producto personalizable */}
              {product.esPersonalizable && (
                <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm font-bold text-purple-900">
                      Producto Personalizable
                    </p>
                    <p className="text-xs text-purple-700">
                      Personaliza la portada, nombre y códigos QR
                    </p>
                  </div>
                </div>
              )}

              {/* Campo de Personalización (sistema antiguo - solo si NO es personalizable) */}
              {product.type === "físico" && !product.esPersonalizable && (
                <div className="space-y-3 p-4 bg-primary/5 rounded-lg border-2 border-primary/20">
                  <Label
                    htmlFor="personalization"
                    className="text-base font-bold text-brown-dark flex items-center gap-2"
                  >
                    <span>⭐</span>
                    Nombre para personalizar (opcional)
                  </Label>
                  <Input
                    id="personalization"
                    placeholder="Ej: María José"
                    maxLength={50}
                    value={personalizedName}
                    onChange={(e) => setPersonalizedName(e.target.value)}
                    className="border-2 border-primary/40 focus:border-primary focus:ring-primary text-base font-medium"
                  />
                  <p className="text-sm text-brown font-semibold flex items-center gap-1">
                    <span>✨</span>
                    Este nombre será grabado en tu producto
                  </p>
                </div>
              )}

              {/* Selector de Tamaño (para productos con opciones de tamaño) */}
              {/* {opcionesTamano && opcionesTamano.length > 0 && (
                <div className="space-y-3 p-4 bg-brown/5 rounded-lg border-2 border-brown/20">
                  <div>
                    <Label className="text-base font-bold text-brown-dark flex items-center gap-2">
                      <span>📏</span>
                      Selecciona el Tamaño
                      <span className="text-red-600">*</span>
                    </Label>
                    {!tamanoSeleccionado && (
                      <p className="text-sm text-brown-light/70 mt-1">
                        Elige un tamaño para continuar
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {opcionesTamano.map((tamano) => (
                      <button
                        key={tamano.id}
                        onClick={() => setTamanoSeleccionado(tamano.id)}
                        className={`
                          relative p-4 rounded-xl border-2 transition-all duration-200 text-left
                          ${tamanoSeleccionado === tamano.id
                            ? 'border-brown bg-brown/10 shadow-md ring-2 ring-brown/20'
                            : 'border-brown-light/30 hover:border-brown/50 bg-white hover:shadow-sm'}
                        `}
                      >
                        {tamanoSeleccionado === tamano.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-brown rounded-full flex items-center justify-center">
                            <span className="text-white text-sm">✓</span>
                          </div>
                        )}

                        
                        <div className="font-bold text-brown-dark text-lg mb-1">
                          {tamano.nombre}
                        </div>

                        
                        <div className="text-brown font-semibold text-xl mb-2">
                          ${tamano.precio}
                        </div>

                        
                        <div className="text-sm text-brown-light/80">
                          {tamano.dimensiones.ancho}cm × {tamano.dimensiones.alto}cm
                        </div>
                        <div className="text-xs text-brown-light/60 capitalize">
                          ({tamano.orientacion})
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )} */}

              {/* Info del Taller — solo si hay al menos un campo lleno */}
              {product.type === "taller" && product.tallerInfo && (
                product.tallerInfo.fechaEvento || product.tallerInfo.lugar ||
                product.tallerInfo.duracion || product.tallerInfo.capacidadMaxima
              ) && (
                <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  {product.tallerInfo!.fechaEvento && (
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <Calendar className="h-4 w-4 shrink-0" />
                      <span>{product.tallerInfo!.fechaEvento}</span>
                    </div>
                  )}
                  {product.tallerInfo!.lugar && (
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <MapPin className="h-4 w-4 shrink-0" />
                      <span>{product.tallerInfo!.lugar}</span>
                    </div>
                  )}
                  {product.tallerInfo!.duracion && (
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <Clock className="h-4 w-4 shrink-0" />
                      <span>{product.tallerInfo!.duracion}</span>
                    </div>
                  )}
                  {product.tallerInfo!.capacidadMaxima && (
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <Users className="h-4 w-4 shrink-0" />
                      <span>Cupos: {product.tallerInfo!.capacidadMaxima} personas</span>
                    </div>
                  )}
                </div>
              )}

              {/* Selector de Cantidad */}
              {(product.type === "digital" || product.type === "taller" || !isOutOfStock) && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cantidad:</label>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-12 text-center font-medium">
                      {quantity}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= maxQuantity}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    {product.type === "físico" && (
                      <span className="text-sm text-muted-foreground">
                        (máx. {maxQuantity})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Botones - según tipo de producto */}
              {product.type === "taller" ? (
                /* Taller: mismo flujo de carrito/checkout que otros productos */
                <Button
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white min-h-[52px] text-base sm:text-lg font-semibold shadow-md touch-manipulation active:scale-[0.98] rounded-full"
                  onClick={handleAddToCart}
                  disabled={!product.isActive}
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  🎨 ¡Inscribirme! — {formatCurrency(product.price)}
                </Button>
              ) : product.esPersonalizable ? (
                <div className="space-y-3">
                  {/* Botón de compra directa (versión estándar con tamaño) */}
                  <Button
                    variant="outline"
                    className="w-full border-2 border-brown-light/40 hover:border-brown-light/60 text-brown-dark hover:bg-white min-h-[48px] text-sm sm:text-base font-medium touch-manipulation active:scale-[0.98]"
                    onClick={handleAddToCart}
                    disabled={isOutOfStock}
                  >
                    <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    <span className="truncate">
                      {isOutOfStock
                        ? "Sin Stock"
                        : `Comprar ahora - ${formatCurrency(
                            obtenerPrecio() * quantity
                          )}`}
                    </span>
                  </Button>

                  {/* Botón de personalización */}
                  <Button
                    className="w-full bg-gradient-to-r from-primary to-primary/80 text-brown-dark hover:from-primary/90 hover:to-primary/70 min-h-[56px] text-base sm:text-lg font-bold shadow-lg touch-manipulation active:scale-[0.98]"
                    onClick={handlePersonalizar}
                    disabled={!product.isActive}
                  >
                    <Sparkles className="h-6 w-6 mr-2" />✨ Personalizar desde{" "}
                    {product.configuracionPersonalizable?.personalizacion
                      ?.opcionesTamano?.[0]?.precio
                      ? formatCurrency(
                          product.configuracionPersonalizable.personalizacion
                            .opcionesTamano[0].precio
                        )
                      : formatCurrency(product.price)}
                  </Button>
                </div>
              ) : (
                /* Botón estándar para productos no personalizables */
                <Button
                  className="w-full bg-primary text-brown hover:bg-primary-dark min-h-[48px] text-base sm:text-lg font-semibold touch-manipulation active:scale-[0.98]"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                >
                  <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="truncate">
                    {isOutOfStock
                      ? "Sin Stock"
                      : `Añadir al Carrito - ${formatCurrency(
                          obtenerPrecio() * quantity
                        )}`}
                  </span>
                </Button>
              )}
            </CardContent>
          </Card>

          <Dialog open={isWtsModalOpen} onOpenChange={setIsWtsModalOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Contáctanos por WhatsApp</DialogTitle>
                <DialogDescription>
                  Déjanos tu email para continuar la conversación.
                </DialogDescription>
              </DialogHeader>

              <input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-3 py-2 mt-4"
              />

              <div className="flex justify-end gap-2 mt-6">
                <button onClick={() => setIsWtsModalOpen(false)}>
                  Cancelar
                </button>
                <button onClick={handleConfirmWTS} disabled={!email}>
                  Ir a WhatsApp
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Información Adicional */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tipo:</span>
                <span className="capitalize">{product.type}</span>
              </div>
              {product.type === "digital" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Entrega:</span>
                  <span>Inmediata por email</span>
                </div>
              )}
              {product.type === "físico" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Envío:</span>
                  <span>Calculado en checkout</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Productos Relacionados */}
      {relatedProducts && relatedProducts.length > 1 && (
        <div className="mt-16">
          <h2 className="text-2xl font-playfair font-bold text-brown mb-6">
            Productos Relacionados
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedProducts
              .filter((p) => p._id !== product._id)
              .slice(0, 4)
              .map((relatedProduct) => (
                <Card
                  key={relatedProduct._id}
                  className="overflow-hidden group hover:shadow-lg transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/products/${relatedProduct._id}`)}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={relatedProduct.images?.[0] || relatedProduct.image}
                      alt={relatedProduct.title}
                      className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium text-sm mb-2 line-clamp-2">
                      {relatedProduct.title}
                    </h3>
                    <div className="text-lg font-bold text-brown">
                      {formatCurrency(relatedProduct.price)}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      )}

      {/* Cart Notification */}
      <CartNotification
        show={notification.show}
        productName={notification.productName}
        onClose={hideNotification}
      />
    </div>
  );
};

export default ProductDetail;
